import { useEffect, useState } from 'react'
import { authApi, describeError, type PhoneStart, type VerifyStatus } from './auth'

/*
 * Ожидание подтверждения номера.
 *
 * При мобильной авторизации оператор подтверждает номер сам, и человек не вводит ничего —
 * но узнать об этом можно только спросив: ответа мы ждём не от пользователя, а от сети.
 * Опрашиваем, пока состояние pending; и на экране кода тоже, реже: оператор может пообещать
 * SMS и потом отказать (19.09.2026, Билайн) — тогда сервер сам переключит проверку на наш
 * код, а нам надо это заметить и перерисовать клетки. Обычная отправка SMS сюда не попадает:
 * там опрашивать нечего.
 */
const PENDING_MS = 3000
const CODE_MS = 5000
const POLL_FOR_MS = 10 * 60_000 // столько живёт код; дальше опрос бессмыслен
// кнопку «не приходит код» показываем не сразу: SMS от оператора обычно доходит за секунды
export const FALLBACK_AFTER_S = 20

export interface VerifyState {
  status: VerifyStatus
  /** Сколько цифр набирать: может смениться на ходу — провайдер отказал, дальше наш шестизначный. */
  codeLength: number
  /** Чем идёт подтверждение сейчас: mobileid — код и SMS у оператора, sms — наш код. */
  channel: string
  /** Проверка начиналась как мобильная авторизация — значит, есть смысл предлагать SMS от Кличата. */
  mobile: boolean
  /** Секунд с начала ожидания кода. */
  elapsed: number
  /** «Не приходит код»: перевести проверку на наш SMS-код. */
  forceSMS: () => void
  forcing: boolean
  forceError: string | null
}

export function useVerifyStatus(start: PhoneStart | null, purpose: string): VerifyState {
  const [status, setStatus] = useState<VerifyStatus>(start?.status ?? 'needs_code')
  const [codeLength, setCodeLength] = useState<number>(start?.code_length ?? 6)
  const [channel, setChannel] = useState<string>(start?.channel ?? 'sms')
  const [elapsed, setElapsed] = useState(0)
  const [forcing, setForcing] = useState(false)
  const [forceError, setForceError] = useState<string | null>(null)

  // новый запрос кода — новое ожидание; сбрасываем на рендере, а не эффектом,
  // иначе один лишний проход покажет состояние от прошлой попытки
  const [seen, setSeen] = useState(start)
  if (seen !== start) {
    setSeen(start)
    setStatus(start?.status ?? 'needs_code')
    setCodeLength(start?.code_length ?? 6)
    setChannel(start?.channel ?? 'sms')
    setElapsed(0)
    setForceError(null)
  }

  const mobile = start?.channel === 'mobileid'

  useEffect(() => {
    const id = start?.verification_id
    // опрашивать есть что только у мобильной авторизации, и только пока она не решилась
    if (!id || !mobile || (status !== 'pending' && status !== 'needs_code') || channel !== 'mobileid') return
    let alive = true
    const startedAt = Date.now()
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_FOR_MS) {
        clearInterval(timer)
        return
      }
      void authApi
        .verifyStatus(id, purpose)
        .then((res) => {
          if (!alive) return
          setStatus(res.status)
          if (res.code_length) setCodeLength(res.code_length)
          if (res.channel) setChannel(res.channel)
        })
        // сеть моргнула или запись истекла: молчим, ответ придёт на следующем такте
        // или человек нажмёт «ввести код руками»
        .catch(() => undefined)
    }, status === 'pending' ? PENDING_MS : CODE_MS)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [start, purpose, status, channel, mobile])

  useEffect(() => {
    if (!start || status === 'ok' || status === 'failed') return
    const t = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [start, status])

  const forceSMS = () => {
    const id = start?.verification_id
    if (!id || forcing) return
    setForcing(true)
    setForceError(null)
    void authApi
      .verifyForceSMS(id, purpose)
      .then((res) => {
        setStatus(res.status)
        if (res.code_length) setCodeLength(res.code_length)
        if (res.channel) setChannel(res.channel)
      })
      .catch((err: unknown) => setForceError(describeError(err).message))
      .finally(() => setForcing(false))
  }

  return { status, codeLength, channel, mobile, elapsed, forceSMS, forcing, forceError }
}
